import { FileText, FileUp, Trash2 } from 'lucide-react';
import { useRef, useState } from 'react';
import { Button } from '../../../../ui/components/ui/button';
import { formatFileSize } from '../../lib/file-size';

const PDF_MIME_TYPE = 'application/pdf';
// Same limit as the upload endpoint (contract-attachments.controller.ts).
const MAX_FILE_SIZE_BYTES = 10000000;

// The endpoint checks the declared type too, so a file without it would only fail there.
const isPdf = (file: File): boolean => file.type === PDF_MIME_TYPE;

export interface PdfUploadFieldProps {
  /** A picked `File`, or an already stored file (name + size). */
  file: Pick<File, 'name' | 'size'> | null;
  /** Called with a valid PDF, or `null` when removed. */
  onChange: (file: File | null) => void;
  /** External error, e.g. "required" from the surrounding form. */
  error?: string | null;
  disabled?: boolean;
}

/** PDF picker following the Learning Suite `CoverUploadField` pattern. */
const PdfUploadField = ({ file, onChange, error, disabled }: PdfUploadFieldProps) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [typeError, setTypeError] = useState<string | null>(null);

  const handlePick = (picked: File | null) => {
    if (!picked) return;
    if (!isPdf(picked)) {
      setTypeError('Nur PDF-Dateien erlaubt.');
      return;
    }
    if (picked.size > MAX_FILE_SIZE_BYTES) {
      setTypeError('Die Datei ist zu groß (max. 10 MB).');
      return;
    }
    setTypeError(null);
    onChange(picked);
  };

  const visibleError = typeError ?? error;

  return (
    <div className="flex flex-col gap-2">
      <input
        ref={inputRef}
        type="file"
        accept={`${PDF_MIME_TYPE},.pdf`}
        className="hidden"
        disabled={disabled}
        onChange={(e) => {
          handlePick(e.target.files?.[0] ?? null);
          // Lets the same file be picked again after removing it.
          e.target.value = '';
        }}
      />

      {file ? (
        <div className="flex items-center gap-4 rounded-xl border border-[#E4E7EC] p-4">
          <FileText className="w-7 h-7 text-[#12282A] shrink-0" />
          <div className="flex-1 min-w-0 flex flex-col">
            <span className="text-[14px] font-bold text-[#12282A] truncate" title={file.name}>
              {file.name}
            </span>
            <span className="text-[12px] text-[#888]">{formatFileSize(file.size)}</span>
          </div>
          <div className="flex gap-2 shrink-0">
            <Button type="button" variant="outline" size="sm" onClick={() => inputRef.current?.click()} disabled={disabled}>
              Ersetzen
            </Button>
            <Button type="button" variant="outline" size="sm" aria-label="Datei entfernen" onClick={() => onChange(null)} disabled={disabled}>
              <Trash2 className="w-4 h-4 text-red-500" />
            </Button>
          </div>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={disabled}
          className="w-full h-28 rounded-xl border-2 border-dashed border-[#E4E7EC] hover:border-[#5FD5DE] hover:bg-[#F0F4FC] transition-colors flex flex-col items-center justify-center gap-2 text-[#888]">
          <FileUp className="w-7 h-7" />
          <span className="text-[14px]">PDF hochladen</span>
          <span className="text-[12px]">Nur PDF-Dateien, Dateiname ohne Umlaute</span>
        </button>
      )}

      {visibleError && <p className="text-[0.8rem] font-medium text-destructive">{visibleError}</p>}
    </div>
  );
};

export default PdfUploadField;
